import { compareVersions, type VersionComparison } from "@ease/page-builder/version-control";
import type { PuckComponent, PuckState } from "@ease/page-builder/ai";
import type { PageVersionSummary } from "@/hooks/use-pages";

function toComparableContent(puckData: unknown): PuckComponent[] {
  if (!puckData || typeof puckData !== "object") {
    return [];
  }

  const data = puckData as {
    content?: unknown[];
    zones?: Record<string, unknown[]>;
  };

  const topLevel = Array.isArray(data.content)
    ? data.content.flatMap((component, index) => normalizeComponent(component, `content:${index}`))
    : [];

  const zoneItems = Object.entries(data.zones ?? {}).flatMap(([zoneId, components]) => (
    Array.isArray(components)
      ? components.flatMap((component, index) => normalizeComponent(component, `${zoneId}:${index}`))
      : []
  ));

  return [...topLevel, ...zoneItems];
}

function normalizeComponent(component: unknown, fallbackId: string): PuckComponent[] {
  if (!component || typeof component !== "object") {
    return [];
  }

  const record = component as {
    type?: unknown;
    props?: unknown;
    _id?: unknown;
  };

  if (typeof record.type !== "string") {
    return [];
  }

  return [{
    type: record.type,
    props: record.props && typeof record.props === "object" ? record.props as Record<string, unknown> : {},
    _id: typeof record._id === "string" ? record._id : fallbackId,
  }];
}

function toComparableState(version: PageVersionSummary): PuckState {
  return {
    content: toComparableContent(version.puckData),
    root: {},
  };
}

export function createVersionComparison(
  versionA: PageVersionSummary,
  versionB: PageVersionSummary,
): VersionComparison | null {
  return compareVersions(
    {
      branches: [{
        id: "main",
        name: "Main",
        branchPointVersionId: versionA.id,
        createdAt: Date.now(),
        isMain: true,
      }],
      versions: [
        {
          id: versionA.id,
          branchId: "main",
          versionNumber: versionA.versionNumber,
          state: toComparableState(versionA),
          description: `Version ${versionA.versionNumber}`,
          action: versionA.generationSource,
          timestamp: new Date(versionA.createdAt).getTime(),
          parentVersionId: null,
        },
        {
          id: versionB.id,
          branchId: "main",
          versionNumber: versionB.versionNumber,
          state: toComparableState(versionB),
          description: `Version ${versionB.versionNumber}`,
          action: versionB.generationSource,
          timestamp: new Date(versionB.createdAt).getTime(),
          parentVersionId: versionA.id,
        },
      ],
      activeBranchId: "main",
      activeVersionId: versionB.id,
    },
    versionA.id,
    versionB.id,
  );
}

export function listChangedPropKeys(
  propsA: Record<string, unknown>,
  propsB: Record<string, unknown>,
): string[] {
  const keys = new Set([...Object.keys(propsA), ...Object.keys(propsB)]);

  return [...keys].filter((key) => JSON.stringify(propsA[key]) !== JSON.stringify(propsB[key]));
}
